import { schrittSichtbar, sichtbareSchritte } from "./ablauf";
import type { Antworten } from "./bedingungen";
import type { Funnel, Schritt } from "./schema";

/**
 * Prüfung der Antworten gegen das Schema.
 *
 * Läuft an zwei Stellen: im Browser, damit der Nutzer sofort sieht, was fehlt,
 * und auf dem Server, bevor ein Lead entsteht. Die Prüfung im Browser ist
 * Bequemlichkeit, die auf dem Server ist die verbindliche. Wer den Funnel
 * umgeht und direkt an die API schreibt, kommt an dieser Stelle nicht vorbei.
 *
 * Die Meldungen sind bewusst ohne Anrede formuliert ("Bitte eine Zahl
 * eingeben" statt "Gib eine Zahl ein"). Ob ein Funnel duzt oder siezt, steht
 * im Funnel-Dokument; diese Prüfung kennt das Dokument nicht und darf deshalb
 * keine Anrede in den Mund nehmen, die zum Rest der Seite nicht passt.
 */

export interface Feldfehler {
  step: string;
  field?: string;
  code: string;
  message: string;
}

export interface Antwortpruefung {
  gueltig: boolean;
  fehler: Feldfehler[];
}

const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Prüft einen einzelnen Schritt. Für die Prüfung im Browser je Bildschirm. */
export function schrittPruefen(schritt: Schritt, wert: unknown): Feldfehler[] {
  const fehler: Feldfehler[] = [];
  const fehlt = wert === undefined || wert === null || wert === "";

  if (schritt.type === "information") return fehler;

  /*
   * Schritte mit mehreren Feldern melden selbst, welches fehlt.
   *
   * Bei einer Frage mit einer Antwort ist "Diese Angabe wird noch gebraucht"
   * eindeutig. Bei vier Kontaktfeldern ist es das nicht: Der Satz steht einmal
   * irgendwo, und der Ausfuellende sucht, welches Feld gemeint ist. Deshalb
   * laufen Kontakt, Einwilligung und Adresse durch die eigene Pruefung, auch
   * wenn noch gar nichts eingegeben wurde - dort steht die Meldung dann unter
   * jedem Feld, das sie betrifft.
   */
  const meldetSelbst =
    schritt.type === "consent" ||
    schritt.type === "contact" ||
    schritt.type === "postcode";

  if (schritt.required && fehlt && !meldetSelbst) {
    return [
      {
        step: schritt.id,
        code: "PFLICHT",
        message: "Diese Angabe wird noch gebraucht.",
      },
    ];
  }

  if (fehlt && !meldetSelbst) return fehler;

  switch (schritt.type) {
    case "single_choice": {
      const erlaubt = schritt.options.map((o) => o.value);
      if (typeof wert !== "string" || !erlaubt.includes(wert)) {
        fehler.push({
          step: schritt.id,
          code: "UNGUELTIGE_AUSWAHL",
          message: "Bitte eine der angebotenen Antworten auswählen.",
        });
      }
      break;
    }

    case "multiple_choice": {
      const erlaubt = schritt.options.map((o) => o.value);
      const werte = Array.isArray(wert) ? (wert as unknown[]) : [];

      if (!werte.every((w) => typeof w === "string" && erlaubt.includes(w))) {
        fehler.push({
          step: schritt.id,
          code: "UNGUELTIGE_AUSWAHL",
          message: "Bitte nur aus den angebotenen Antworten auswählen.",
        });
        break;
      }

      if (werte.length < schritt.min_select) {
        fehler.push({
          step: schritt.id,
          code: "ZU_WENIG",
          message: `Bitte mindestens ${String(schritt.min_select)} Antworten auswählen.`,
        });
      }
      if (schritt.max_select !== undefined && werte.length > schritt.max_select) {
        fehler.push({
          step: schritt.id,
          code: "ZU_VIELE",
          message: `Bitte höchstens ${String(schritt.max_select)} Antworten auswählen.`,
        });
      }

      // Eine ausschließende Option neben anderen ergibt eine widersprüchliche
      // Antwort ("Dämmung und nichts davon").
      const ausschliessend = schritt.options
        .filter((o) => o.exclusive)
        .map((o) => o.value);
      if (werte.length > 1 && werte.some((w) => ausschliessend.includes(w as string))) {
        fehler.push({
          step: schritt.id,
          code: "WIDERSPRUCH",
          message: "Diese Auswahl schließt die anderen aus.",
        });
      }
      break;
    }

    case "text": {
      const text = typeof wert === "string" ? wert : "";
      if (text.length < schritt.min_length) {
        fehler.push({
          step: schritt.id,
          code: "ZU_KURZ",
          message: `Bitte mindestens ${String(schritt.min_length)} Zeichen eingeben.`,
        });
      }
      if (text.length > schritt.max_length) {
        fehler.push({
          step: schritt.id,
          code: "ZU_LANG",
          message: `Höchstens ${String(schritt.max_length)} Zeichen.`,
        });
      }
      break;
    }

    case "number": {
      const zahl = typeof wert === "number" ? wert : Number(wert);
      if (!Number.isFinite(zahl)) {
        fehler.push({
          step: schritt.id,
          code: "KEINE_ZAHL",
          message: "Bitte eine Zahl eingeben.",
        });
        break;
      }
      if (schritt.min !== undefined && zahl < schritt.min) {
        fehler.push({
          step: schritt.id,
          code: "ZU_KLEIN",
          message: `Bitte mindestens ${String(schritt.min)}.`,
        });
      }
      if (schritt.max !== undefined && zahl > schritt.max) {
        fehler.push({
          step: schritt.id,
          code: "ZU_GROSS",
          message: `Bitte höchstens ${String(schritt.max)}.`,
        });
      }
      break;
    }

    case "postcode": {
      /*
       * Zwei Formen: eine Zeichenkette, solange der Schritt nur die
       * Postleitzahl abfragt, ein Objekt sobald Ort oder Straße dazukommen.
       * Beide müssen gelesen werden - sonst verliert jemand mitten im
       * Ausfüllen seine Eingaben, weil zwischendurch ausgeliefert wurde.
       */
      const teile: Record<string, unknown> =
        typeof wert === "object" && wert !== null && !Array.isArray(wert)
          ? (wert as Record<string, unknown>)
          : { postcode: wert };

      const text = (feld: string): string =>
        typeof teile[feld] === "string" ? teile[feld].trim() : "";

      if (!/^\d{5}$/.test(text("postcode").replace(/\s/g, ""))) {
        fehler.push({
          step: schritt.id,
          // Mit Feldnamen, damit die Meldung unter der Postleitzahl steht.
          // Ohne ihn landete sie am Ende der Gruppe - also unter der Strasse.
          field: "postcode",
          code: "UNGUELTIGE_PLZ",
          message: "Bitte eine fünfstellige Postleitzahl eingeben.",
        });
      }

      for (const feld of schritt.address_fields) {
        if (feld.required && text(feld.name) === "") {
          fehler.push({
            step: schritt.id,
            // Mit Feldnamen, damit die Meldung unter dem Feld steht, das sie
            // meint. Zwei fehlende Angaben ergeben sonst eine Meldung an einer
            // Stelle, und die zweite sucht man.
            field: feld.name,
            code: "PFLICHTFELD",
            // Dieselbe Formulierung wie im Kontaktschritt. Zwei Sätze für
            // dieselbe Sache lesen sich wie zwei verschiedene Probleme.
            message: "Dieses Feld muss ausgefüllt werden",
          });
        }
      }
      break;
    }

    case "date": {
      const text = typeof wert === "string" ? wert : "";
      if (Number.isNaN(new Date(text).getTime())) {
        fehler.push({
          step: schritt.id,
          code: "UNGUELTIGES_DATUM",
          message: "Bitte ein gültiges Datum angeben.",
        });
      }
      break;
    }

    case "contact": {
      const daten =
        typeof wert === "object" && wert !== null
          ? (wert as Record<string, unknown>)
          : {};

      for (const posten of schritt.consent_items) {
        if (posten.required && daten[posten.key] !== true) {
          fehler.push({
            step: schritt.id,
            field: posten.key,
            code: "EINWILLIGUNG_FEHLT",
            message: "Bitte stimmen Sie zu, damit wir Ihre Anfrage weitergeben dürfen",
          });
        }
      }

      for (const feld of schritt.fields) {
        const eingabe = daten[feld.name];
        const leer = typeof eingabe !== "string" || eingabe.trim() === "";

        if (feld.required && leer) {
          fehler.push({
            step: schritt.id,
            field: feld.name,
            code: "PFLICHT",
            // Ausformuliert statt knapp: "Bitte ausfüllen" steht unter vier
            // Feldern gleich und sagt nicht, dass es ein Pflichtfeld ist.
            message: "Dieses Feld muss ausgefüllt werden",
          });
          continue;
        }
        if (leer) continue;

        const text = eingabe.trim();

        if (feld.validate === "email" && !EMAIL_MUSTER.test(text)) {
          fehler.push({
            step: schritt.id,
            field: feld.name,
            code: "UNGUELTIGE_EMAIL",
            message: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
          });
        }

        if (feld.validate === "phone_de") {
          // Nur grob: mindestens sechs Ziffern. Die genaue Aufbereitung macht
          // der Server, siehe apps/api/src/modules/leads/normalisierung.ts.
          // Eine strenge Prüfung im Browser würde gültige Nummern abweisen,
          // die nur ungewöhnlich geschrieben sind.
          const ziffern = text.replace(/\D/g, "");
          if (ziffern.length < 6) {
            fehler.push({
              step: schritt.id,
              field: feld.name,
              code: "UNGUELTIGE_NUMMER",
              message: "Bitte geben Sie eine gültige Telefonnummer ein",
            });
          }
        }
      }
      break;
    }

    case "consent": {
      const daten =
        typeof wert === "object" && wert !== null
          ? (wert as Record<string, unknown>)
          : {};

      for (const eintrag of schritt.items) {
        if (eintrag.required && daten[eintrag.key] !== true) {
          fehler.push({
            step: schritt.id,
            field: eintrag.key,
            code: "EINWILLIGUNG_FEHLT",
            message: "Diese Zustimmung wird gebraucht, um die Anfrage weiterzugeben.",
          });
        }
      }
      break;
    }
  }

  return fehler;
}

/**
 * Prüft einen vollständigen Antwortsatz.
 *
 * Das ist die Prüfung, die auf dem Server läuft, bevor ein Lead entsteht.
 * Geprüft wird nur, was der Nutzer nach seinen eigenen Antworten überhaupt
 * gesehen haben kann: Eine übersprungene Frage darf nicht als fehlend gelten.
 */
export function antwortenPruefen(funnel: Funnel, antworten: Antworten): Antwortpruefung {
  const fehler: Feldfehler[] = [];

  for (const schritt of sichtbareSchritte(funnel, antworten)) {
    fehler.push(...schrittPruefen(schritt, antworten[schritt.id]));
  }

  // Antworten auf Schritte, die es nicht gibt oder die dieser Nutzer nie
  // gesehen hat, deuten auf eine manipulierte Anfrage hin. Sie werden nicht
  // abgewiesen, aber gemeldet, damit sie nicht stillschweigend im Lead landen.
  for (const id of Object.keys(antworten)) {
    const schritt = funnel.steps.find((s) => s.id === id);
    if (!schritt) {
      fehler.push({
        step: id,
        code: "UNBEKANNTER_SCHRITT",
        message: "Zu diesem Schritt gibt es keine Frage im Funnel.",
      });
    } else if (!schrittSichtbar(schritt, antworten)) {
      fehler.push({
        step: id,
        code: "NICHT_SICHTBAR",
        message: "Diese Frage war für diesen Ablauf nicht vorgesehen.",
      });
    }
  }

  return { gueltig: fehler.length === 0, fehler };
}

/** Entfernt Antworten auf Schritte, die nicht sichtbar sind. */
export function antwortenBereinigen(funnel: Funnel, antworten: Antworten): Antworten {
  const sichtbar = new Set(sichtbareSchritte(funnel, antworten).map((s) => s.id));
  const sauber: Antworten = {};

  for (const [id, wert] of Object.entries(antworten)) {
    if (sichtbar.has(id)) sauber[id] = wert;
  }

  return sauber;
}
